import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  Card,
  Input,
  Label,
} from "reactstrap";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import {
  FaFileExcel,
  FaUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaDownload,
  FaTrash,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { ApiBulkCreateUsuariosSaefman } from "../../../api/saefman";

const BATCH_SIZE = 10;
const PARALLEL_REQUESTS = 3;

/** Parsea "APELLIDO1 APELLIDO2 NOMBRE1 NOMBRE2" -> { apellidos, nombres } */
function parseNombreToApellidosNombres(nombreCompleto) {
  const s = (nombreCompleto || "").toString().trim();
  if (!s) return { apellidos: "", nombres: "" };
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length <= 2) {
    return {
      apellidos: parts[0] || "",
      nombres: parts[1] || parts[0] || "",
    };
  }
  const nombres = parts.slice(-2).join(" ");
  const apellidos = parts.slice(0, -2).join(" ");
  return { apellidos, nombres };
}

const SaefmanExcelUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validRows, setValidRows] = useState([]);
  const [processResult, setProcessResult] = useState(null);
  const [processingProgress, setProcessingProgress] = useState({
    current: 0,
    total: 0,
    success: 0,
    errors: 0,
    currentBatch: 0,
    totalBatches: 0,
  });
  const [errorList, setErrorList] = useState([]);

  const clearFileState = useCallback(() => {
    setFile(null);
    setExcelData(null);
    setValidationErrors([]);
    setValidRows([]);
    setProcessResult(null);
    setErrorList([]);
    setProcessingProgress({
      current: 0,
      total: 0,
      success: 0,
      errors: 0,
      currentBatch: 0,
      totalBatches: 0,
    });
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    const f = acceptedFiles[0];
    if (f) {
      setFile(f);
      setExcelData(null);
      setValidationErrors([]);
      setProcessResult(null);
      setValidRows([]);
      validateExcelFile(f);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024,
  });

  function validateExcelFile(file) {
    setIsValidating(true);
    setValidationErrors([]);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const sheetName = wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(ws);
        if (!json || json.length === 0) {
          setValidationErrors(["El archivo no tiene filas de datos."]);
          setIsValidating(false);
          return;
        }
        const first = json[0];
        const keys = Object.keys(first).map((k) => (k || "").trim().toUpperCase());
        const hasCodigo = keys.some((k) => /CODIGO|CODE/.test(k));
        const hasNombre = keys.some((k) => /NOMBRE/.test(k));
        const hasCedula = keys.some((k) => /CEDULA|CI|USUARIO/.test(k));
        if (!hasCodigo || !hasNombre || !hasCedula) {
          setValidationErrors([
            "El Excel debe tener columnas: CODIGO, NOMBRE y CEDULA (o variaciones).",
          ]);
          setIsValidating(false);
          return;
        }
        const codigoKey = Object.keys(first).find((k) =>
          /CODIGO|CODE/i.test((k || "").trim())
        );
        const nombreKey = Object.keys(first).find((k) =>
          /NOMBRE/i.test((k || "").trim())
        );
        const cedulaKey = Object.keys(first).find((k) =>
          /CEDULA|CI|USUARIO/i.test((k || "").trim())
        );
        const rows = [];
        const errs = [];
        json.forEach((row, idx) => {
          const codigo = row[codigoKey] != null ? String(row[codigoKey]).trim() : "";
          const nombre = row[nombreKey] != null ? String(row[nombreKey]).trim() : "";
          const cedula = row[cedulaKey] != null ? String(row[cedulaKey]).trim() : "";
          if (!cedula) {
            errs.push(`Fila ${idx + 2}: CEDULA vacía`);
            return;
          }
          if (!nombre) {
            errs.push(`Fila ${idx + 2}: NOMBRE vacío`);
            return;
          }
          const { apellidos, nombres } = parseNombreToApellidosNombres(nombre);
          rows.push({
            code: codigo || null,
            usuario: cedula,
            nombres,
            apellidos,
          });
        });
        if (errs.length > 0) {
          setValidationErrors(errs.slice(0, 20));
          if (errs.length > 20) {
            setValidationErrors((e) => [...e, `... y ${errs.length - 20} más`]);
          }
        }
        setExcelData(json);
        setValidRows(rows);
        setIsValidating(false);
        if (rows.length > 0) {
          toast.success(`${rows.length} registros válidos listos para procesar`);
        }
      } catch (err) {
        setValidationErrors([`Error al leer el archivo: ${err.message}`]);
        setIsValidating(false);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  const handleProcessFile = async () => {
    if (!validRows.length) {
      toast.error("Cargue un archivo válido.");
      return;
    }
    setIsProcessing(true);
    setProcessResult(null);
    setErrorList([]);
    const batches = [];
    for (let i = 0; i < validRows.length; i += BATCH_SIZE) {
      batches.push(validRows.slice(i, i + BATCH_SIZE));
    }
    setProcessingProgress({
      current: 0,
      total: validRows.length,
      success: 0,
      errors: 0,
      currentBatch: 0,
      totalBatches: batches.length,
    });

    try {
      let totalSuccess = 0;
      let totalProcessed = 0;
      const allErrors = [];
      for (let i = 0; i < batches.length; i += PARALLEL_REQUESTS) {
        const chunk = batches.slice(i, i + PARALLEL_REQUESTS);
        const promises = chunk.map((batch) =>
          ApiBulkCreateUsuariosSaefman({ users: batch })
        );
        const results = await Promise.all(promises);
        for (let r = 0; r < results.length; r++) {
          const res = results[r];
          const batch = chunk[r];
          totalProcessed += batch ? batch.length : 0;
          if (res.sms === "ok" && res.data) {
            totalSuccess += res.data.created || 0;
            if (res.data.errors && res.data.errors.length) {
              res.data.errors.forEach((e) =>
                allErrors.push(`Fila aprox. ${e.index + 2}: ${e.message}`)
              );
            }
          } else {
            allErrors.push(res.mensaje || "Error en lote");
          }
        }
        setProcessingProgress({
          current: totalProcessed,
          total: validRows.length,
          success: totalSuccess,
          errors: allErrors.length,
          currentBatch: Math.min(i + PARALLEL_REQUESTS, batches.length),
          totalBatches: batches.length,
        });
      }
      setProcessResult({
        sms: allErrors.length === 0 ? "ok" : "partial",
        mensaje: `Procesamiento completado: ${totalSuccess} creados, ${allErrors.length} errores.`,
        data: {
          createdUsers: totalSuccess,
          errors: allErrors,
        },
      });
      setErrorList(allErrors);
      if (allErrors.length === 0) {
        toast.success(`Se crearon ${totalSuccess} usuarios.`);
        onSuccess?.();
      } else {
        toast(`Creados: ${totalSuccess}. Errores: ${allErrors.length}`, { icon: "⚠️" });
      }
    } catch (err) {
      toast.error("Error al procesar el archivo.");
      setProcessResult({
        sms: "err",
        mensaje: err.message || "Error inesperado",
        data: { createdUsers: 0, errors: [err.message] },
      });
      setErrorList([err.message]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (isProcessing) {
      if (!window.confirm("El procesamiento está en curso. ¿Cerrar de todos modos?")) return;
    }
    clearFileState();
    onClose();
  };

  useEffect(() => {
    const h = (e) => {
      if (isProcessing) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [isProcessing]);

  const progressPct =
    processingProgress.total > 0
      ? Math.round((processingProgress.current / processingProgress.total) * 100)
      : 0;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl" centered>
      <ModalHeader toggle={isProcessing ? undefined : handleClose}>
        <div className="d-flex align-items-center">
          <FaFileExcel className="me-2 text-primary" />
          Carga masiva usuarios Saefman
        </div>
      </ModalHeader>
      <ModalBody>
        <Alert color="info" className="mb-3">
          <strong>Formato del Excel:</strong> Columnas <strong>CODIGO</strong>,{" "}
          <strong>NOMBRE</strong> (apellidos + nombres en una celda) y{" "}
          <strong>CEDULA</strong>. El sistema separa automáticamente apellidos y nombres.
        </Alert>

        <div className="mb-3">
          <Button
            color="outline-primary"
            size="sm"
            onClick={() => {
              const ws = XLSX.utils.aoa_to_sheet([
                ["CODIGO", "NOMBRE", "CEDULA"],
                ["017776", "ACOSTA PINCAY JENNIFER ELIZABETH", "1312278912"],
                ["021375", "AGUAYO GARCIA CRISTHIAN BERNARDO", "1312274382"],
              ]);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, "Saefman");
              XLSX.writeFile(wb, "plantilla_usuarios_saefman.xlsx");
              toast.success("Plantilla descargada");
            }}
          >
            <FaDownload className="me-1" />
            Descargar plantilla
          </Button>
        </div>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded p-4 text-center ${
            isDragActive ? "border-primary bg-light" : "border-secondary"
          }`}
          style={{ cursor: "pointer" }}
        >
          <input {...getInputProps()} />
          <FaUpload size={32} className="text-muted mb-2" />
          <h6>
            {isDragActive ? "Suelta el archivo" : "Arrastra el Excel aquí o haz clic"}
          </h6>
          <small className="text-muted">.xlsx, .xls (máx. 10MB)</small>
        </div>

        {file && (
          <div className="mt-3 d-flex align-items-center justify-content-between p-3 border rounded">
            <div>
              <FaFileExcel className="text-success me-2" />
              {file.name} — {validRows.length} registros válidos
            </div>
            <Button color="outline-danger" size="sm" onClick={clearFileState}>
              <FaTrash className="me-1" /> Quitar
            </Button>
          </div>
        )}

        {validationErrors.length > 0 && (
          <Alert color="danger" className="mt-3">
            <ul className="mb-0">
              {validationErrors.slice(0, 15).map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </Alert>
        )}

        {isValidating && (
          <div className="mt-3 d-flex align-items-center">
            <Spinner size="sm" className="me-2" />
            Validando archivo...
          </div>
        )}

        {isProcessing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4">
            <Card className="border-0 shadow" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
              <div className="card-body text-white">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    <Spinner size="sm" className="me-2" />
                    Procesando en paralelo...
                  </h5>
                  <div className="text-end">
                    <h4 className="mb-0">
                      {processingProgress.current} / {processingProgress.total}
                    </h4>
                    <small>{progressPct}%</small>
                  </div>
                </div>
                <div
                  style={{
                    height: "32px",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: "16px",
                    overflow: "hidden",
                    border: "2px solid rgba(255,255,255,0.3)",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${progressPct}%`,
                      backgroundColor: "rgba(255,255,255,0.95)",
                      transition: "width 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      color: "#667eea",
                      fontSize: "14px",
                    }}
                  >
                    {progressPct}%
                  </div>
                </div>
                <Row className="text-center mt-3">
                  <Col md={4}>
                    <div className="p-2 bg-white bg-opacity-25 rounded text-dark">
                      <h6 className="mb-0 fw-bold">{processingProgress.success}</h6>
                      <small className="text-dark">Creados</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-2 bg-white bg-opacity-25 rounded text-dark">
                      <h6 className="mb-0 fw-bold">{processingProgress.errors}</h6>
                      <small className="text-dark">Errores</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-2 bg-white bg-opacity-25 rounded text-dark">
                      <h6 className="mb-0 fw-bold">
                        Lote {processingProgress.currentBatch}/{processingProgress.totalBatches}
                      </h6>
                      <small className="text-dark">Lotes</small>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card>
          </motion.div>
        )}

        <AnimatePresence>
          {processResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4"
            >
              <Card className="border-0 shadow">
                <div
                  className="card-header text-white"
                  style={{
                    background:
                      processResult.data?.errors?.length > 0
                        ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                        : "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                  }}
                >
                  <div className="d-flex align-items-center">
                    {processResult.data?.errors?.length > 0 ? (
                      <FaExclamationTriangle className="me-2" size={24} />
                    ) : (
                      <FaCheckCircle className="me-2" size={24} />
                    )}
                    <h5 className="mb-0">Procesamiento completado</h5>
                  </div>
                </div>
                <div className="card-body">
                  <Row>
                    <Col md={6}>
                      <div className="text-center p-3 border rounded">
                        <FaCheckCircle className="text-success mb-2" size={28} />
                        <h4 className="text-success mb-0">
                          {processResult.data?.createdUsers ?? 0}
                        </h4>
                        <small className="text-muted">Usuarios creados</small>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="text-center p-3 border rounded">
                        <FaExclamationTriangle className="text-danger mb-2" size={28} />
                        <h4 className="text-danger mb-0">
                          {processResult.data?.errors?.length ?? 0}
                        </h4>
                        <small className="text-muted">Errores</small>
                      </div>
                    </Col>
                  </Row>
                  {errorList.length > 0 && (
                    <Alert color="danger" className="mt-3 mb-0">
                      <strong>Detalle de errores:</strong>
                      <div
                        style={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          fontSize: "0.875rem",
                        }}
                      >
                        {errorList.slice(0, 50).map((msg, i) => (
                          <div key={i} className="mb-1 border-start border-3 border-danger ps-2">
                            {msg}
                          </div>
                        ))}
                        {errorList.length > 50 && (
                          <div className="text-muted">... y {errorList.length - 50} más</div>
                        )}
                      </div>
                    </Alert>
                  )}
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </ModalBody>
      <ModalFooter>
        <Button color="outline-secondary" onClick={handleClose} disabled={isProcessing}>
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={handleProcessFile}
          disabled={isProcessing || validRows.length === 0 || isValidating}
        >
          {isProcessing ? (
            <>
              <Spinner size="sm" className="me-2" />
              Procesando...
            </>
          ) : (
            <>
              <FaCheckCircle className="me-2" />
              Procesar archivo ({validRows.length} registros)
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SaefmanExcelUploadModal;
